import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Table, TableBody, TableHead, Toolbar, Typography, CircularProgress,
    TablePagination, Chip, Box, FormControl, InputLabel, Select, MenuItem, Button
} from "@mui/material";
import { listSupportTickets } from "../services/supportService";
import { SupportTicket } from "../types/Support";
import {
    StyledTableContainer, StyledTableCell, StyledTableRow, StyledPaper,
} from "../assets/styles/tableStyle"; // Assuming you reuse your styles

const PAGE_SIZE = 20;

const statusColor = (status: string) => {
    switch (status) {
        case 'open': return 'error';
        case 'in_progress': return 'warning';
        case 'resolved': return 'success';
        case 'closed': return 'default';
        default: return 'default';
    }
};

const SupportTicketList: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Initial filters from URL (e.g., coming from Right-Click on Ride)
    const initialRideId = searchParams.get('rideId') ? Number(searchParams.get('rideId')) : undefined;

    const [items, setItems] = useState<SupportTicket[]>([]);
    const [page, setPage] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);

    // Filters
    const [statusFilter, setStatusFilter] = useState<string>("");
    const [issueFilter, setIssueFilter] = useState<string>("");

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listSupportTickets(page + 1, PAGE_SIZE, {
                status: statusFilter || undefined,
                issueType: issueFilter || undefined,
                rideId: initialRideId
            });
            setItems(res.data.data);
            setTotal(res.data.total);
        } catch (error) {
            console.error("Failed to fetch tickets", error);
        } finally {
            setLoading(false);
        }
    }, [page, statusFilter, issueFilter, initialRideId]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    return (
        <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2 }}>
            <Toolbar sx={{ justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h6">
                        Support Tickets {initialRideId && `(Filtered for Ride #${initialRideId})`}
                    </Typography>
                    {initialRideId && (
                        <Button size="small" onClick={() => navigate('/support-tickets')}>
                            Clear Ride Filter
                        </Button>
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="open">Open</MenuItem>
                            <MenuItem value="in_progress">In Progress</MenuItem>
                            <MenuItem value="resolved">Resolved</MenuItem>
                            <MenuItem value="closed">Closed</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Issue Type</InputLabel>
                        <Select value={issueFilter} label="Issue Type" onChange={(e) => setIssueFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="payment_issue">Payment</MenuItem>
                            <MenuItem value="ride_issue">Ride</MenuItem>
                            <MenuItem value="app_issue">App/Bug</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Toolbar>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <StyledTableContainer>
                    <Table>
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>ID</StyledTableCell>
                                <StyledTableCell>Issue Type</StyledTableCell>
                                <StyledTableCell>Description</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Created At</StyledTableCell>
                                <StyledTableCell>Created By (UID)</StyledTableCell>
                                <StyledTableCell>Related Ride</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((ticket) => (
                                <StyledTableRow
                                    key={ticket.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/support-ticket/${ticket.id}`)}
                                >
                                    <StyledTableCell>#{ticket.id}</StyledTableCell>
                                    <StyledTableCell>{ticket.issueType}</StyledTableCell>
                                    <StyledTableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {ticket.description}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip label={ticket.status} color={statusColor(ticket.status) as any} size="small" variant="filled" />
                                    </StyledTableCell>
                                    <StyledTableCell>{new Date(ticket.createdAt).toLocaleString()}</StyledTableCell>
                                    <StyledTableCell>
                                        <span style={{ fontFamily: 'monospace' }}>{ticket.createdByUid.substring(0, 8)}...</span>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {ticket.chatRoom?.rideId ? `#${ticket.chatRoom.rideId}` : '-'}
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={PAGE_SIZE}
                        rowsPerPageOptions={[PAGE_SIZE]}
                    />
                </StyledTableContainer>
            )}
        </StyledPaper>
    );
};

export default SupportTicketList;